<script setup>
import { getWhatsAppDisplayContent } from 'dashboard/helper/whatsappMessagePresentation';
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import CaptainGenerationDetails from '../CaptainGenerationDetails.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, SENDER_TYPES } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
  frameless: { type: Boolean, default: false },
});

const {
  variant,
  externalMeta,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  id,
  sender,
  senderType,
  reactions,
  onReactionClick,
} = useMessageContext();
const { t } = useI18n();

const isCaptainMessage = computed(
  () =>
    (sender.value?.type ?? senderType.value) === SENDER_TYPES.CAPTAIN_ASSISTANT
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);

const emailMetaClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : ''
);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  if (props.frameless) return ['text-n-slate-12'];
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

// The conversation loads earlier messages until the quoted one is shown.
const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
    fromQuote: true,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !externalMeta?.value &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// A reply is quoted as one quiet line, "回复 <sender>：<text>", as in chat apps.
const replyToSender = computed(() => {
  const replied = inReplyTo?.value;
  if (!replied) return '';
  const name =
    replied.sender?.availableName ||
    replied.sender?.available_name ||
    replied.sender?.name;
  if (name) return name;
  return (replied.messageType ?? replied.message_type) === 1 ? '我方' : '客户';
});

const replyToPreview = computed(() => {
  if (!inReplyTo?.value) return '';

  const { attachments } = inReplyTo.value;
  const content = getWhatsAppDisplayContent(inReplyTo.value);

  if (content) {
    return new MessageFormatter(content).plainText.replace(/\s+/g, ' ').trim();
  }
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="flex min-w-0 max-w-full mb-1.5 ps-2 border-s-[3px] border-n-slate-7 text-xs leading-5 text-n-slate-11 cursor-pointer hover:text-n-slate-12"
      :class="{ 'mx-2 mt-2': frameless }"
      :title="replyToPreview"
      data-reply-preview
      @click="scrollToMessage"
    >
      <span class="truncate">回复 {{ replyToSender }}：{{ replyToPreview }}</span>
    </div>
    <slot />
    <div
      v-if="reactions?.length"
      data-message-reactions
      class="mt-2 flex min-w-0 max-w-full flex-wrap gap-1"
      :class="{ 'px-3 pb-3': frameless }"
    >
      <button
        v-for="reaction in reactions"
        :key="reaction.key"
        type="button"
        :title="reaction.label"
        :aria-label="reaction.label"
        class="inline-flex min-w-0 max-w-full cursor-pointer items-center gap-2 rounded-full bg-n-alpha-2 px-2.5 py-1 text-sm leading-5 text-n-slate-11 hover:bg-n-alpha-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        @click.stop="onReactionClick?.()"
      >
        <span class="shrink-0" aria-hidden="true">{{ reaction.emoji }}</span>
        <span class="h-3 shrink-0 border-l border-n-weak" aria-hidden="true" />
        <span class="min-w-0 truncate">{{ reaction.author }}</span>
      </button>
    </div>
    <template v-if="shouldShowMeta">
      <CaptainGenerationDetails
        v-if="isCaptainMessage"
        :message-id="id"
        class="mt-2"
      >
        <template #meta>
          <MessageMeta :class="[emailMetaClass, metaColorClass]" />
        </template>
      </CaptainGenerationDetails>
      <MessageMeta
        v-else
        :class="[flexOrientationClass, emailMetaClass, metaColorClass]"
        class="mt-2"
      />
    </template>
  </div>
</template>
